import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { OnScreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';
import { LoggingService } from '@logging/logging.service';
import { ErrorHandlerService } from '@utils/error-handler.service';
import { AbstractEntityStateService } from '@entity-framework/entity-record/state/abstract-entity.state-service';
import { UpdatePasswordDataProvider } from './update-password.data-provider';
import { UpdatePasswordFormData } from './update-password.model';
import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable()
export class UpdatePasswordStateService extends AbstractEntityStateService<UpdatePasswordFormData> {
  private static storeName = 'update-password';
  private linkTypeIdSubject = new BehaviorSubject<string | null>(null);
  private linkIdSubject = new BehaviorSubject<string | null>(null);

  readonly linkTypeId$ = this.linkTypeIdSubject.asObservable();
  readonly linkId$ = this.linkIdSubject.asObservable();

  constructor(
    protected override dataProvider: UpdatePasswordDataProvider,
    protected override route: ActivatedRoute,
    protected override errorHandler: ErrorHandlerService,
    protected override messaging: OnScreenMessagingService,
    protected override logger: LoggingService,
    private formBuilder: FormBuilder
  ) {
    super(UpdatePasswordStateService.storeName, dataProvider, route, errorHandler, messaging, logger);
  }

  setLinkParams(linkTypeId: string, linkId: string): void {
    this.linkTypeIdSubject.next(linkTypeId);
    this.linkIdSubject.next(linkId);
  }

  setUpForm() {
    this.bindFormGroup((_updatePasswordFormData: UpdatePasswordFormData | undefined) => {
      this.logger.trace('UpdatePasswordStateService: binding form to entity models: ', _updatePasswordFormData);
      const form = this.createForm();

      form.get('password')?.valueChanges.subscribe(() => {
        form.get('passwordConfirmation')?.updateValueAndValidity();
      });

      return form;
    });
  }

  private createForm() {
    return this.formBuilder.group({
      password: [
        '',
        [Validators.required, Validators.minLength(12), this.validatePassword()]
      ],
      passwordConfirmation: ['', [Validators.required, this.matchValues('password')]]
    });
  }

  private validatePassword(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumeric = /[0-9]/.test(value);
      const hasSpecialChar = /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/.test(value);

      const valid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar;

      return !valid ? { invalidPassword: true } : null;
    };
  }

  private matchValues(matchTo: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) return null;
      return control.value === control.parent?.get(matchTo)?.value ? null : { notMatching: true };
    };
  }

  override persist$(): Observable<UpdatePasswordFormData> {
    const linkTypeId = this.linkTypeIdSubject.value;
    const linkId = this.linkIdSubject.value;

    if (!linkTypeId || !linkId) {
      this.messaging.warn('Link parameters are required');
      return throwError(() => new Error('Link parameters are required'));
    }

    return this.dataProvider.updatePassword$(this.formGroup?.value, linkTypeId, linkId).pipe(
      switchMap((response) => {
        if (response.errorMessages?.length > 0) {
          this.validationErrors = {
            passwordConfirmation: response.errorMessages
              .map((entry) => entry.message)
              .filter(Boolean)
              .join(', ')
          };
          return EMPTY;
        }

        this.validationErrors = undefined;
        return of(this.formGroup?.value as UpdatePasswordFormData);
      }),
      catchError((err) => {
        const validationErrors = this.errorHandler.extractValidationErrors(this.adaptPersistError(err));
        if (validationErrors) {
          const message =
            typeof validationErrors === 'string'
              ? validationErrors
              : Object.values(validationErrors).flat().join(', ');

          this.validationErrors = { passwordConfirmation: message };
          return this.errorHandler.handlePersistError(err, false, UpdatePasswordStateService.storeName, {
            suppressToast: true
          });
        }

        return this.errorHandler.handlePersistError(err, false, UpdatePasswordStateService.storeName);
      })
    );
  }

  /** Reshapes API error bodies so {@link ErrorHandlerService.extractValidationErrors} can parse them. */
  private adaptPersistError(err: any): any {
    const messageItems = err?.error?.message;
    if (!Array.isArray(messageItems)) {
      return err;
    }

    return {
      ...err,
      status: 400,
      error: messageItems
    };
  }
}
